import { inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';
import {
  SnackbarComponent,
  SnackbarData,
  SnackbarVariant,
} from '../../shared/notification/snackbar.component';
import { LayoutService } from './layout.service';

/**
 * Toaster over MatSnackBar rendering the shared NABP alert card (SnackbarComponent,
 * Figma 1083-112463). Each variant is a coloured toast with a bold title and an
 * optional supporting message. Errors stay up longer and announce assertively.
 * Desktop toasts sit top-right; mobile toasts are top-centred and full width, just
 * under the top bar (Figma 1289-342977 — sizing/offset in styles.scss).
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly snackBar = inject(MatSnackBar);
  private readonly isHandset = inject(LayoutService).isHandset;

  success(title: string, message?: string): MatSnackBarRef<SnackbarComponent> {
    return this.show('success', title, message, 5000);
  }

  error(title: string, message?: string): MatSnackBarRef<SnackbarComponent> {
    return this.show('error', title, message, 8000);
  }

  info(title: string, message?: string): MatSnackBarRef<SnackbarComponent> {
    return this.show('info', title, message, 5000);
  }

  warning(title: string, message?: string): MatSnackBarRef<SnackbarComponent> {
    return this.show('warning', title, message, 6000);
  }

  private show(
    variant: SnackbarVariant,
    title: string,
    message: string | undefined,
    duration: number,
  ): MatSnackBarRef<SnackbarComponent> {
    const config: MatSnackBarConfig<SnackbarData> = {
      duration,
      horizontalPosition: this.isHandset() ? 'center' : 'right',
      verticalPosition: 'top',
      politeness: variant === 'error' ? 'assertive' : 'polite',
      // Strips MDC's default surface chrome so the card shows (see styles.scss).
      panelClass: 'csd-snackbar-panel',
      data: { variant, title, message },
    };
    return this.snackBar.openFromComponent(SnackbarComponent, config);
  }
}
